import dynamic from "next/dynamic";

import { useProductDetailsContext } from "../ProductDetailsProvider";
import { ProductDetailsInfoNewPrice } from "./ProductDetailsInfoNewPrice";
import { ProductBySlugNonNullable } from "@/common/contracts/pdp.contracts";
import { decodeHtmlEntitiesOrNull } from "@/common/utils/html-entities.utils";
import { ProductDetailsInfoBidBuyStickyWrapper } from "@/components/ProductDetail/ProductDetailsInfoBidBuyStickyWrapper";
import { ProductDetailsInfoSeller } from "@/components/ProductDetail/info/seller/ProductDetailsInfoSeller";
import { ProductDetailsBrandPromises } from "@/components/ProductDetail/info/usp/ProductDetailsBrandPromises";
import { ProductDetailsDelivery } from "@/components/ProductDetail/info/usp/ProductDetailsDelivery";
import { DesignerItemLabel } from "@/features/designer-item/components/DesignerItemLabel";
import { PdpReservationSection } from "@/features/pro-buyer-reservations/components/PdpReservationSection";
import { PdpProjectChipSlot } from "@/features/pro-buyer-workspaces/components/WorkspaceSlots";
import { ProductDetailsUsShippingNotice } from "@/features/us-market/components/ProductDetailsUsShippingNotice";
import { Heading } from "@/ui/cva/Heading";
import { ProductDetailsFavoriteButton } from "@whoppah/components/ProductDetail/ProductDetailsFavoriteButton";
import { ProductDetailsShareButton } from "@whoppah/components/ProductDetail/ProductDetailsShareButton";
import { ProductDetailsInfoActivity } from "@whoppah/components/ProductDetail/info/ProductDetailsInfoActivity";
import { ProductDetailsInfoAttributes } from "@whoppah/components/ProductDetail/info/ProductDetailsInfoAttributes";
import { ProductDetailsInfoBidBuy } from "@whoppah/components/ProductDetail/info/ProductDetailsInfoBidBuy";
import { ProductDetailsInfoDescription } from "@whoppah/components/ProductDetail/info/ProductDetailsInfoDescription";
import ProductDetailsInfoImportCharges from "@whoppah/components/ProductDetail/info/ProductDetailsInfoImportCharges";
import { ProductDetailsInfoPrice } from "@whoppah/components/ProductDetail/info/ProductDetailsInfoPrice";
import { ProductDetailsInfoTags } from "@whoppah/components/ProductDetail/info/ProductDetailsInfoTags";

const ProductDetailsInfoEmbeddedMessages = dynamic(() =>
  import(
    "@/components/ProductDetail/info/ProductDetailsInfoEmbeddedMessages"
  ).then(module => module.ProductDetailsInfoEmbeddedMessages)
);

const SHOW_DESIGNER_ITEM_LABEL = false;

type ProductDetailsInfoProps = {
  product: ProductBySlugNonNullable;
  className?: string;
};

export const ProductDetailsInfo = ({
  product,
  className,
}: ProductDetailsInfoProps) => {
  const { showOriginalText } = useProductDetailsContext();

  return (
    <div className={className}>
      <div className="flex flex-col space-y-6">
        <div className="flex flex-col space-y-4">
          <ProductDetailsInfoEmbeddedMessages />
          <div className="flex justify-between space-x-2">
            <span>
              <Heading as="h1" size="h3">
                {decodeHtmlEntitiesOrNull(
                  showOriginalText ? product?.original_title : product?.title
                )}
              </Heading>
            </span>
            <span className="hidden space-x-2 md:flex">
              <ProductDetailsShareButton />
              <ProductDetailsFavoriteButton product={product} />
            </span>
          </div>
          {SHOW_DESIGNER_ITEM_LABEL && (
            <DesignerItemLabel product={product} className="w-max" />
          )}
          <ProductDetailsInfoPrice product={product} />
          {/* WHO-6629: Pro Buyers in the workspace gate only; renders nothing for anyone else. */}
          <PdpProjectChipSlot product={product} />
          <hr className="col-span-1 md:hidden" />
        </div>
        <ProductDetailsInfoNewPrice product={product} />
        <ProductDetailsInfoTags product={product} />
        <ProductDetailsInfoActivity
          product={product}
          className="hidden md:flex"
        />
        <hr className="hidden md:block" />
      </div>
      <ProductDetailsInfoBidBuyStickyWrapper>
        <ProductDetailsInfoBidBuy product={product} />
      </ProductDetailsInfoBidBuyStickyWrapper>
      <PdpReservationSection product={product} />
      <div className="flex flex-col space-y-6">
        <ProductDetailsBrandPromises />
        <ProductDetailsUsShippingNotice product={product} />
        <ProductDetailsInfoImportCharges product={product} />
        <ProductDetailsDelivery product={product} />
        <ProductDetailsInfoSeller product={product} />
        <ProductDetailsInfoDescription product={product} />
        <ProductDetailsInfoAttributes product={product} />
      </div>
    </div>
  );
};
